import { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import { UXDataTable } from "../../components/UXDataTable";
import Template from "../../api/LIMS/Template";
import { Modal, ModalWrapper } from "./ModalWrapper";
import { LoadingMoreStatus } from "./LoadingMoreStatus";
import { Box } from "@mui/material";
import { ColumnConfig, CsvExportPayload, MenuItem } from "./types";

export const MyTemplateCols: ColumnConfig[] = [
  {
    field: "templateName",
    headerName: "Template Name",
    type: "button-text-template",
    path: "/newSample",
  },
  { field: "templateType", headerName: "Template Type", type: "label" },
  { field: "templateOwnerEmail", headerName: "Template Owner", type: "label" },
  { field: "isPublic", headerName: "Is Public", type: "boolean" },
  { field: "createdDate", headerName: "Create Date", type: "datetime" },
  { field: "lastModifiedDate", headerName: "Last Modified", type: "datetime" },
  { field: "messages", headerName: "Messages", type: "list-strings" },
];

export interface TemplatesTableProps {
  onExportPayloadChange: (csvPayLoad: CsvExportPayload) => void;
}

export const TemplatesTable = ({ onExportPayloadChange }: TemplatesTableProps) => {
  const [templatesIsLoading, setTemplatesIsLoading] = useState<boolean>(true);
  const [myTemplates, setMyTemplates] = useState<Template[] | null>([]);
  const [refreshTemplatesTrigger, setRefreshTemplatesTrigger] = useState<boolean>(false);
  const [modal, setModal] = useState<Modal | null>(null);
  const closeModal = () => setModal(null);
  const okButton = { text: "OK", action: closeModal };
  const { username, isInternalUser } = useUser();

  const deleteTemplate = (template: Template) => {
    if (template) {
      if (username !== template.templateOwnerEmail) {
        setModal({
          title: "Template Error",
          description: "You cannot delete a template from another user!",
          button: okButton,
        });
        return;
      }

      Template.delete(template.templateName, "sample").then((res) => {
        if (res.message === "Success") {
          setModal({
            title: "Template Deleted",
            description: "Template successfully deleted!",
            button: okButton,
          });
          setRefreshTemplatesTrigger((prev) => !prev);
        } else {
          setModal({
            title: "Template Failed to Delete",
            description: `${res.message}. Contact support if you feel this is an error.`,
            button: okButton,
          });
        }
      });
    }
  };

  const handleDeleteTemplate = (template: Template) => {
    if (template) {
      setModal({
        title: `Delete Template`,
        description: "Are you sure?",
        button: { text: "Yes", action: () => deleteTemplate(template) },
        secondaryButton: { text: "No", action: closeModal },
      });
    }
  };

  const changeTemplatePublicStatus = (template: Template) => {
    template.isPublic = !template.isPublic;

    Template.update(template).then((res) => {
      if (res.message === "Success") {
        setModal({
          title: "Template Updated",
          description: "Template successfully updated!",
          button: okButton,
        });
        setRefreshTemplatesTrigger((prev) => !prev);
      } else {
        setModal({
          title: "Template Failed to Update",
          description: `${res.message}. Contact support if you feel this is an error.`,
          button: okButton,
        });
      }
    });
  };

  const handleChangeTemplatePublicStatus = (template: Template) => {
    if (template) {
      if (username !== template.templateOwnerEmail) {
        setModal({
          title: "Template Error",
          description: "You cannot update a template from another user!",
          button: okButton,
        });
        return;
      }

      changeTemplatePublicStatus(template);
    }
  };

  const menuItemsTemplates: MenuItem[] = [
    {
      menuType: "text",
      redirectPath: "",
      text: "Delete Template",
      onClickFunction: handleDeleteTemplate,
    },
    ...(isInternalUser
      ? [
          {
            menuType: "text",
            redirectPath: "",
            text: "Change Public Status",
            onClickFunction: handleChangeTemplatePublicStatus,
          },
        ]
      : []),
  ];

  useEffect(() => {
    let cancelPromise = false;

    setTemplatesIsLoading(true);
    setMyTemplates([]);

    async function getTemplateData(
      perPage: number,
      page: number,
      allData: any[] = [],
    ): Promise<any[]> {
      const response = await Template.getMyTemplates(
        "",
        "sample",
        isInternalUser ?? false,
        true,
        page,
        perPage,
      );

      if (cancelPromise) return allData;

      if (response === null || response.length === 0) {
        setTemplatesIsLoading(false);
        return allData;
      } else {
        allData = [...allData, ...response];
        page++;
        setMyTemplates(allData);
        return getTemplateData(perPage, page, allData);
      }
    }

    getTemplateData(20, 1);

    return () => {
      cancelPromise = true;
    };
  }, [username, refreshTemplatesTrigger, isInternalUser]);

  useEffect(() => {
    const csvPayload = prepareExportPayload();
    onExportPayloadChange(csvPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myTemplates, templatesIsLoading]);

  const prepareExportPayload = () => {
    const csvExport: CsvExportPayload = {
      fileName: "Templates",
      objList: (myTemplates ?? [])?.map((template) => {
        return {
          "Template Name": template.templateName,
          "Template Type": template.templateType,
          "Template Owner": template.templateOwnerEmail,
          "Is Public": template.isPublic,
          "Create Date": template.createdDate,
          "Last Modified": template.lastModifiedDate,
          Messages: template.messages,
        };
      }),
      disabled: templatesIsLoading || myTemplates === null || myTemplates.length === 0,
    };
    return csvExport;
  };

  const noDataTemplatesMessage = `No sample templates found for owner ${username}`;

  return (
    <>
      <Box display="flex" justifyContent="flex-end" width="100%">
        {templatesIsLoading && myTemplates && myTemplates.length > 0 && (
          <LoadingMoreStatus currentCount={myTemplates.length} />
        )}
      </Box>
      <UXDataTable
        tableWidth="100%"
        cols={MyTemplateCols}
        rows={myTemplates === null ? [] : myTemplates}
        moreOptionsCell={true}
        enablePaging={true}
        noDataMessage={noDataTemplatesMessage}
        menuProps={menuItemsTemplates}
        defaultRowsPerPage={10}
        isDataLoading={templatesIsLoading && (myTemplates === null || myTemplates.length === 0)}
        tableName={"sampleTemplates"}
        enableSorting={true}
        dataRefreshTrigger={undefined}
        setDataRefreshTrigger={undefined}
      />
      {modal !== null && <ModalWrapper modal={modal} />}
    </>
  );
};
